import React, { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useErrorStore, Toast as ToastType } from '@/stores/errorStore';

interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = React.memo(({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const toastId = toast.id;
  const toastDuration = toast.duration;

  useEffect(() => {
    if (toastDuration && toastDuration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toastDuration);

      return () => clearTimeout(timer);
    }
  }, [toastId, toastDuration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-300" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-rose-300" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-300" />;
      case 'info':
        return <Info className="w-5 h-5 text-violet-300" />;
      default:
        return <Info className="w-5 h-5 text-white/60" />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-emerald-500/10 border-emerald-500/30';
      case 'error':
        return 'bg-rose-500/10 border-rose-500/30';
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/30';
      case 'info':
        return 'bg-violet-500/10 border-violet-500/30';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  const getTitleColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-emerald-100';
      case 'error':
        return 'text-rose-100';
      case 'warning':
        return 'text-amber-100';
      case 'info':
        return 'text-violet-100';
      default:
        return 'text-white';
    }
  };

  const getMessageColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-emerald-100/80';
      case 'error':
        return 'text-rose-100/80';
      case 'warning':
        return 'text-amber-100/80';
      case 'info':
        return 'text-violet-100/80';
      default:
        return 'text-white/70';
    }
  };

  return (
    <div
      className={`
        mm-glass
        ${getBackgroundColor()}
        border rounded-2xl shadow-2xl p-4 mb-3 min-w-[300px] sm:min-w-[320px] max-w-md
        transform transition-all duration-300 ease-in-out
        ${isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
      `}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5" aria-hidden="true">
          {getIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {toast.title && (
            <h3 className={`text-sm font-semibold ${getTitleColor()} mb-1 break-words`}>
              {toast.title}
            </h3>
          )}
          <p className={`text-sm ${getMessageColor()} break-words`}>
            {toast.message}
          </p>
        </div>

        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 ml-2 text-white/50 hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 rounded-lg p-1 touch-target"
          aria-label={`Dismiss ${toast.type} notification`}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
});

ToastItem.displayName = 'ToastItem';

const ToastContainer: React.FC = React.memo(() => {
  const toasts = useErrorStore((state) => state.toasts);
  const removeToast = useErrorStore((state) => state.removeToast);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col items-end pointer-events-none max-w-full px-4 sm:px-0"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="pointer-events-auto w-full sm:w-auto">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onDismiss={removeToast}
          />
        ))}
      </div>
    </div>
  );
});

ToastContainer.displayName = 'ToastContainer';

export default ToastContainer;
